import Http from "../http";
import { describeRequestError } from "../last_error";

// SAM-257: every write below returns `{ success: false, data: null }` on
// failure. It used to return `[]` or `{}` — both truthy — so callers checking
// `if (result)` or destructuring `{ success, data }` read a hard failure as a
// completed write. Confirmed live in production 2026-07-28 on createJob (the
// create-job wizard showed a success screen and created nothing). Reads below
// still return `[]`/`null` on failure: for a list render an empty list is the
// intended degraded state, and the callers treat it as such.

// A job whose jobType is missing (deleted job type, or a create that dropped
// the field — see SAM-043) comes back from the API without `jobType` at all.
// Every row renderer downstream dereferences `job.jobType.icon` and
// `job.jobType.settings.previewSettings` unguarded, so ONE such job throws
// during render and the error boundary sends the whole list to /error/500 —
// the entire jobs page down, tenant-wide, for every user. Confirmed live on
// pink 2026-07-28 (job #7326). Normalising here, the one funnel every jobs-list
// render routes through, is the root-cause fix; the alternative is a guard at
// each of ~40 read sites, all of which then render the same empty state this
// produces anyway.
const withJobType = (job) =>
  job && !job.jobType ? { ...job, jobType: { settings: {} } } : job;

// Exactly the same failure, one level down. `settings` is optional on a
// question (3 of job #7347's 4 questions have no such field), and the panel
// dereferences `question.settings.X` unguarded in ~50 places —
// question_summary's `question.settings.managementNote` is simply the first
// one render reaches. Confirmed live on pink 2026-08-20: TypeError, whole page
// to /error/500. api/src/job/job.service.ts guarantees the field now; this is
// the same guarantee on the client side of the wire, for the job shapes that
// do not come from viewJob.
const withQuestionSettings = (job) => {
  if (!job || !Array.isArray(job.questions)) return job;
  if (job.questions.every((q) => q && q.settings)) return job;
  return {
    ...job,
    questions: job.questions.map((q) =>
      q && !q.settings ? { ...q, settings: {} } : q
    ),
  };
};

const withJobDefaults = (job) => withQuestionSettings(withJobType(job));

const normalizeJobs = (data) => {
  if (Array.isArray(data)) return data.map(withJobDefaults);
  if (data && Array.isArray(data.docs))
    return { ...data, docs: data.docs.map(withJobDefaults) };
  return withJobDefaults(data);
};

// Exposed for jobs.normalize.test.js only — these two shape guarantees are the
// difference between a rendered job panel and a tenant-wide /error/500, so they
// are worth pinning directly rather than through a component test.
export const __test__ = { normalizeJobs, withJobType, withQuestionSettings };

export const getJobsList = async (params, version = "v1") => {
  const path = `/${version}/jobs`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params: params,
    });
    return normalizeJobs(response.data);
  } catch (e) {
    return [];
  }
};

export const getCurrentJob = async (params, version = "v1") => {
  const path = `/${version}/jobs/${params.id}`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);
    // This is the call the job panel loads a single job with, and it was the
    // one read path that skipped normalizeJobs entirely — which is why the
    // list rendered fine while every /jobs/:id deep link died.
    return normalizeJobs(response.data);
  } catch (e) {
    return [];
  }
};

export const createJob = async (params) => {
  const path = `/v2/jobs`;
  try {
    const response = await Http.sharedInstance.withAuth().post(path, params);
    return response.data;
  } catch (e) {
    // ponytail: don't swallow the error into a truthy `[]` (callers destructure
    // `{ success, data }` — a bare array made `success` undefined-but-truthy
    // downstream in some call sites and hid every real rejection as "created").
    return { success: false, data: null };
  }
};

export const cloneJob = async (params) => {
  const path = `/v2/jobs/${params.id}/clone`;
  try {
    const response = await Http.sharedInstance.withAuth().post(path);
    return response.data;
  } catch (e) {
    // SAM-271: keep the reason. A clone failure is a 400, and http.js only
    // records errors at >= 500, so discarding it here left no trace of the
    // failure anywhere — the user got a fixed sentence and nobody could say
    // what the server had actually objected to.
    return { success: false, data: describeRequestError(e) };
  }
};

export const removePrevious = async (idJob, targetJob) => {
  const path = `/v2/jobs/${idJob}/remove_prev/${targetJob}`;
  try {
    const response = await Http.sharedInstance.withAuth().put(path);

    return response.data;
  } catch (e) {
    return { success: false, data: null };
  }
};

export const addPrevious = async (idJob, targetJob) => {
  const path = `/v2/jobs/${idJob}/add_prev/${targetJob}`;
  try {
    const response = await Http.sharedInstance.withAuth().put(path);

    return response.data;
  } catch (e) {
    return { success: false, data: null };
  }
};

export const toggleAttendance = async (jobId, userId) => {
  const path = `/v2/jobs/${jobId}/attendance/${userId}`;
  try {
    const response = await Http.sharedInstance.withAuth().put(path);
    return response.data;
  } catch (e) {
    return { success: false, data: null };
  }
};

export const deleteJobs = async (params) => {
  const path = `/v2/jobs/${params.id}`;
  try {
    const response = await Http.sharedInstance.withAuth().delete(path);
    return response.data;
  } catch (e) {
    return { success: false, data: null };
  }
};

export const deleteMultipleJobs = async (params) => {
  const path = `/v2/jobs/multiple`;
  try {
    const response = await Http.sharedInstance.withAuth().delete(path, {
      data: {
        id: params["ids"],
      },
    });
    return response.data;
  } catch (e) {
    return { success: false, data: null };
  }
};

export const updateJob = async (params) => {
  const path = `/v2/jobs/${params.id}`;
  try {
    const response = await Http.sharedInstance.withAuth().put(path, params);
    return response.data;
  } catch (e) {
    return { success: false, data: null };
  }
};

export const assignMultiple = async (params) => {
  const path = `/v2/jobs/assign_multiple`;
  try {
    const response = await Http.sharedInstance.withAuth().put(path, params);
    return response.data;
  } catch (e) {
    return { success: false, data: null };
  }
};

export const assignSingle = async (params) => {
  const path = `/v2/jobs/${params.id}/assign`;
  try {
    const response = await Http.sharedInstance.withAuth().put(path, {
      users: params.users,
    });
    return response.data;
  } catch (e) {
    return { success: false, data: null };
  }
};

export const feedsJobs = async (params) => {
  const path = `/v2/jobs/${params.id}/feeds?page=${params.page}&page_size=${params.page_size}`;
  try {
    const response = await Http.sharedInstance.getClient().get(path);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: [],
    };
  }
};

export const visit = async (params) => {
  const path = `/v2/jobs/${params.id}/visit`;
  try {
    const response = await Http.sharedInstance.getClient().put(path);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: [],
    };
  }
};

export const view = async (id) => {
  const path = `/v2/jobs/${id}`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);

    return response.data;
  } catch (e) {
    return null;
  }
};

export const viewJob = async (id) => {
  const path = `/v2/jobs/${id}`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);

    return response.data;
  } catch (e) {
    return null;
  }
};

/**
 * @deprecated Not used
 * @returns {Promise<*|{data: null, success: boolean}>}
 */
export const jobsDashboard = async () => {
  const path = `/v2/jobs/dashboard`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);

    return response.data;
  } catch (e) {
    return {
      success: false,
      data: null,
    };
  }
};

/**
 * @deprecated Not used
 * @returns {Promise<*|{data: (*|null), success: boolean}>}
 */
export const recentJobsDashboard = async () => {
  const path = `/v2/jobs/dashboard/recent`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);

    return response.data;
  } catch (e) {
    return {
      success: false,
      data: e.response ? e.response.data : null,
    };
  }
};

/**
 * @deprecated Not used
 * @returns {Promise<*|{data: null, success: boolean}>}
 */
export const statusCounters = async () => {
  const path = `/v2/jobs/dashboard/counters`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path);

    return response.data;
  } catch (e) {
    return {
      success: false,
      data: null,
    };
  }
};

export const assignManyToMany = async (assignmentArray) => {
  const path = `/v2/jobs/assign_many`;
  try {
    const response = await Http.sharedInstance.withAuth().post(path, {
      jobs: assignmentArray,
    });

    return response.data;
  } catch (e) {
    return {
      success: false,
      data: null,
    };
  }
};

export const exportJob = async (id) => {
  let jobID = id;
  const combined = [];
  if (Array.isArray(jobID)) {
    jobID = jobID[0];
    // remove the first element and combine the rest
    combined.push(...id.slice(1));
  }
  let path = `/v2/jobs/${jobID}/export`;

  if (combined.length > 0) {
    path += `?combined=${combined.join(",")}`;
  }
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      responseType: "blob",
    });

    const headers = response.headers;
    const filename = headers["content-disposition"].split("filename=")[1];

    const link = document.createElement("a");
    link.target = "_blank";
    link.download = filename;
    link.href = URL.createObjectURL(
      new Blob([response.data], { type: "application/pdf" })
    );
    link.click();
    return null;
  } catch (e) {
    // `e.response` is undefined when the request never reached the server, and
    // reading `.data` off it threw a TypeError out of this catch — so a failed
    // export rejected instead of returning, taking the caller's handler with
    // it. Same family as SAM-257.
    const blob = e.response ? e.response.data : null;
    alert(blob ? await blob.text() : e.message || "Export failed");
    return {
      success: false,
      data: null,
    };
  }
};
